import { useState, useEffect } from 'react';
import { X, Loader2 } from 'lucide-react';
import { Button } from './ui/Button';
import { api } from '../utils/api';
import toast from 'react-hot-toast';

export function DiscountCodeModal({ discount, onClose, onSave }) {
  const isEditMode = !!discount;

  const [formData, setFormData] = useState({
    code: '',
    title: '',
    description: '',
    discount_type: 'percentage',
    value: '',
    usage_limit: '',
    one_use_per_customer: true,
    minimum_purchase_amount: '',
    starts_at: '',
    ends_at: '',
    applies_to_all_products: true,
    product_variant_gids: [],
  });

  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (discount) {
      // Format dates for datetime-local input
      const formatDateForInput = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toISOString().slice(0, 16);
      };

      setFormData({
        code: discount.code || '',
        title: discount.title || '',
        description: discount.description || '',
        discount_type: discount.discount_type || 'percentage',
        value: discount.value?.toString() || '',
        usage_limit: discount.usage_limit?.toString() || '',
        one_use_per_customer: discount.usage_limit_per_customer === 1,
        minimum_purchase_amount: discount.minimum_purchase_amount?.toString() || '',
        starts_at: formatDateForInput(discount.starts_at),
        ends_at: formatDateForInput(discount.ends_at),
        applies_to_all_products: discount.applies_to_all_products ?? true,
        product_variant_gids: discount.product_variant_gids || [],
      });
    } else {
      // Set default start date to now for new discounts
      const now = new Date();
      setFormData(prev => ({
        ...prev,
        starts_at: now.toISOString().slice(0, 16),
      }));
    }
  }, [discount]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    setErrors(prev => ({ ...prev, [field]: null }));
  };

  const validateForm = () => {
    const newErrors = {};

    // Required fields
    if (!formData.code.trim()) {
      newErrors.code = 'Code ist erforderlich';
    } else if (!/^[A-Z0-9_-]+$/.test(formData.code)) {
      newErrors.code = 'Code darf nur Großbuchstaben, Zahlen, - und _ enthalten';
    }

    if (!formData.title.trim()) {
      newErrors.title = 'Titel ist erforderlich';
    }

    if (!formData.value || parseFloat(formData.value) <= 0) {
      newErrors.value = 'Wert muss größer als 0 sein';
    }

    if (formData.discount_type === 'percentage' && parseFloat(formData.value) > 100) {
      newErrors.value = 'Prozentwert kann nicht größer als 100 sein';
    }

    if (!formData.starts_at) {
      newErrors.starts_at = 'Startdatum ist erforderlich';
    }

    // Validate end date is after start date
    if (formData.starts_at && formData.ends_at) {
      const start = new Date(formData.starts_at);
      const end = new Date(formData.ends_at);
      if (end <= start) {
        newErrors.ends_at = 'Enddatum muss nach Startdatum liegen';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Bitte überprüfen Sie die Eingaben');
      return;
    }

    setIsSaving(true);

    try {
      // Prepare data for API
      const submitData = {
        code: formData.code.toUpperCase().trim(),
        title: formData.title.trim(),
        description: formData.description.trim() || null,
        discount_type: formData.discount_type,
        value: parseFloat(formData.value),
        usage_limit: formData.usage_limit ? parseInt(formData.usage_limit, 10) : null,
        usage_limit_per_customer: formData.one_use_per_customer ? 1 : null,
        minimum_purchase_amount: formData.minimum_purchase_amount
          ? parseFloat(formData.minimum_purchase_amount)
          : null,
        starts_at: new Date(formData.starts_at).toISOString(),
        ends_at: formData.ends_at ? new Date(formData.ends_at).toISOString() : null,
        applies_to_all_products: formData.applies_to_all_products,
        product_variant_gids: formData.applies_to_all_products ? [] : formData.product_variant_gids,
      };

      let response;
      if (isEditMode) {
        response = await api.updateDiscountCode(discount.id, submitData);
      } else {
        response = await api.createDiscountCode(submitData);
      }

      if (response.success) {
        toast.success(isEditMode ? 'Rabattcode aktualisiert' : 'Rabattcode erstellt');
        onSave();
        onClose();
      } else {
        toast.error(response.error || 'Fehler beim Speichern');
      }
    } catch (error) {
      console.error('Error saving discount:', error);
      toast.error(error.message || 'Fehler beim Speichern');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">
            {isEditMode ? 'Rabattcode bearbeiten' : 'Neuer Rabattcode'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Basis-Informationen</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Code <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => handleChange('code', e.target.value.toUpperCase())}
                  disabled={isEditMode}
                  placeholder="SUMMER25"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent font-mono ${
                    errors.code ? 'border-red-500' : 'border-gray-300'
                  } ${isEditMode ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                />
                {errors.code && (
                  <p className="text-sm text-red-500 mt-1">{errors.code}</p>
                )}
                {isEditMode && (
                  <p className="text-xs text-gray-500 mt-1">Code kann nicht geändert werden</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Titel <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                  placeholder="Sommer Sale 2025"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${
                    errors.title ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.title && (
                  <p className="text-sm text-red-500 mt-1">{errors.title}</p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Beschreibung
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Optional: Zusätzliche Beschreibung"
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
          </div>

          {/* Discount Configuration */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Rabatt-Konfiguration</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Typ <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.discount_type}
                  onChange={(e) => {
                    handleChange('discount_type', e.target.value);
                    handleChange('value', ''); // Reset value when type changes
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value="percentage">Prozentual (%)</option>
                  <option value="fixed_amount">Fester Betrag (€)</option>
                  <option value="free_shipping">Kostenloser Versand</option>
                </select>
              </div>

              {formData.discount_type !== 'free_shipping' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Wert <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      step={formData.discount_type === 'percentage' ? '1' : '0.01'}
                      min="0"
                      max={formData.discount_type === 'percentage' ? '100' : undefined}
                      value={formData.value}
                      onChange={(e) => handleChange('value', e.target.value)}
                      placeholder={formData.discount_type === 'percentage' ? '25' : '20.00'}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${
                        errors.value ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">
                      {formData.discount_type === 'percentage' ? '%' : '€'}
                    </span>
                  </div>
                  {errors.value && (
                    <p className="text-sm text-red-500 mt-1">{errors.value}</p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Restrictions */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Einschränkungen</h3>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nutzungslimit
              </label>
              <input
                type="number"
                min="1"
                value={formData.usage_limit}
                onChange={(e) => handleChange('usage_limit', e.target.value)}
                placeholder="Unbegrenzt"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">Leer = unbegrenzt</p>
            </div>

            <div>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.one_use_per_customer}
                  onChange={(e) => handleChange('one_use_per_customer', e.target.checked)}
                  className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                />
                <span className="text-sm text-gray-700">Nur eine Nutzung pro Kunde</span>
              </label>
              <p className="text-xs text-gray-500 mt-1">Jeder Kunde kann den Code nur einmal verwenden</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Mindestbestellwert (€)
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={formData.minimum_purchase_amount}
                onChange={(e) => handleChange('minimum_purchase_amount', e.target.value)}
                placeholder="Kein Minimum"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
          </div>

          {/* Validity Period */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Gültigkeit</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Startdatum <span className="text-red-500">*</span>
                </label>
                <input
                  type="datetime-local"
                  value={formData.starts_at}
                  onChange={(e) => handleChange('starts_at', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${
                    errors.starts_at ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.starts_at && (
                  <p className="text-sm text-red-500 mt-1">{errors.starts_at}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Enddatum
                </label>
                <input
                  type="datetime-local"
                  value={formData.ends_at}
                  onChange={(e) => handleChange('ends_at', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${
                    errors.ends_at ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.ends_at && (
                  <p className="text-sm text-red-500 mt-1">{errors.ends_at}</p>
                )}
                <p className="text-xs text-gray-500 mt-1">Leer = kein Ablaufdatum</p>
              </div>
            </div>
          </div>

          {/* Product Restrictions */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Produkte</h3>

            <div>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.applies_to_all_products}
                  onChange={(e) => handleChange('applies_to_all_products', e.target.checked)}
                  className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                />
                <span className="text-sm text-gray-700">Für alle Produkte gültig</span>
              </label>
            </div>

            {!formData.applies_to_all_products && (
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600">
                  Produktspezifische Einschränkungen werden in einer zukünftigen Version hinzugefügt.
                  Aktuell gilt der Code für alle Produkte.
                </p>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              disabled={isSaving}
            >
              Abbrechen
            </Button>
            <Button
              type="submit"
              disabled={isSaving}
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Speichere...
                </>
              ) : (
                isEditMode ? 'Aktualisieren' : 'Erstellen'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
